import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';

const BANNER_IMAGE =
  'https://roadtechservices.netlify.app/images/snowblower_banner.png';
const DETAIL_IMAGE =
  'https://roadtechservices.netlify.app/images/snowblower_0.jpg';
const SIDE_IMAGE =
  'https://roadtechservices.netlify.app/images/snowblower_1.jpg';
const CTA_IMAGE =
  'https://roadtechservices.netlify.app/images/mower_banner-850w.jpeg';

const whatWeService = [
  'Single-stage snow blowers',
  'Two-stage snow blowers',
  'Seasonal tune-ups & inspections',
  'Belt & spark plug replacement',
  'Carburetor cleaning & repair',
  'Auger & chute repairs',
  'Starting & ignition issues',
  'Oil changes & maintenance',
  'Pre-season preparation',
];

const faqs = [
  {
    q: 'What types of snow blowers do you repair?',
    a: 'We service both single-stage and two-stage snow blowers from all major brands.',
  },
  {
    q: 'Do I have to bring my snow blower to a shop?',
    a: 'No. Road Tech Services comes directly to your home for convenient mobile repair.',
  },
  {
    q: 'Can repairs be done the same day?',
    a: "Yes — if the issue is described correctly and parts are on hand, most repairs are completed the same day.",
  },
  {
    q: 'Do you perform seasonal maintenance?',
    a: 'Absolutely. We provide pre-season tune-ups so your snow blower is winter-ready when you need it.',
  },
  {
    q: 'What brands do you work on?',
    a: 'Certified in Briggs & Stratton, Stihl (Silver Level), Kohler, Kawasaki, and more.',
  },
];

export default function SnowBlowerRepair() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  return (
    <div className="font-body">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-40 pb-24 min-h-[420px] flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src={BANNER_IMAGE}
            alt="Snow Blower Repair"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/65" />
        </div>
        <div className="relative z-10 text-center px-6 max-w-3xl mx-auto">
          <span className="inline-block bg-orange-600 text-white text-xs font-semibold px-4 py-1.5 rounded-full mb-4">
            Snow Blowers
          </span>
          <h1 className="font-display text-5xl lg:text-7xl font-bold text-white uppercase tracking-tight">
            Snow Blower Repair & Maintenance
          </h1>
        </div>
      </section>

      {/* Main Content */}
      <section className="bg-white py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div className="relative h-[520px] rounded-2xl overflow-hidden lg:sticky lg:top-28">
              <img
                src={DETAIL_IMAGE}
                alt="Mobile snow blower repair"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/70 to-transparent">
                <div className="flex items-center gap-2 text-white text-sm font-semibold">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-tools-line text-orange-400" />
                  </div>
                  Certified Mobile Repairs
                </div>
              </div>
            </div>

            <div>
              <p className="text-orange-600 text-xs font-bold uppercase tracking-widest mb-4">
                Certified • Mobile • Same-Day
              </p>
              <h2 className="font-display text-4xl font-bold uppercase tracking-tight text-neutral-900 mb-5 leading-tight">
                Snow Blower Service by Road Tech Services
              </h2>
              <p className="text-neutral-600 text-base leading-relaxed mb-4">
                At Road Tech Services, we provide professional snow blower repair
                and maintenance right at your doorstep. Whether it&rsquo;s a
                clogged auger, starting issues, or a full tune-up before the
                season, we&rsquo;ve got you covered.
              </p>
              <p className="text-neutral-600 text-base leading-relaxed mb-8">
                Our technician is Master Certified with Briggs &amp; Stratton
                and also certified in Stihl, Kohler, and Kawasaki — bringing
                years of expertise to every repair. Don&rsquo;t get caught
                unprepared when the first snowfall hits.
              </p>

              <div className="bg-stone-50 rounded-xl p-6 mb-8">
                <p className="text-neutral-900 font-semibold text-xs mb-5 uppercase tracking-wider">
                  What We Service
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {whatWeService.map((item) => (
                    <div key={item} className="flex items-center gap-3 text-sm text-neutral-600">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className="ri-checkbox-circle-fill text-orange-500 text-base" />
                      </div>
                      {item}
                    </div>
                  ))}
                </div>
              </div>

              <div className="mb-8">
                <p className="text-neutral-900 font-semibold text-xs mb-5 uppercase tracking-wider">
                  Our Process
                </p>
                <div className="space-y-4">
                  {[
                    'Call or book your snow blower service',
                    'Get a clear repair estimate upfront',
                    'We arrive at your home for on-site service',
                    'Most repairs are completed the same day',
                  ].map((step, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-7 h-7 rounded-full bg-orange-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                        {i + 1}
                      </div>
                      <p className="text-neutral-600 text-sm leading-relaxed pt-1">{step}</p>
                    </div>
                  ))}
                </div>
              </div>

              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-semibold px-7 py-3.5 rounded-lg transition-colors text-sm whitespace-nowrap cursor-pointer"
              >
                Schedule Snow Blower Service
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Side Image Section */}
      <section className="bg-stone-50 py-24 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="h-[400px] rounded-2xl overflow-hidden">
              <img src={SIDE_IMAGE} alt="Snow blower tune-up" className="w-full h-full object-cover object-top" />
            </div>
            <div>
              <p className="text-orange-600 text-xs font-bold uppercase tracking-widest mb-4">
                Snow Blower Care
              </p>
              <h2 className="font-display text-3xl lg:text-4xl font-bold uppercase tracking-tight text-neutral-900 mb-5 leading-tight">
                Professional Snow Blower Repair & Maintenance
              </h2>
              <p className="text-neutral-600 text-base leading-relaxed mb-6">
                From replacing spark plugs and belts to fixing starting issues,
                our certified mobile technician ensures your snow blower is ready
                when you need it most. We come to you — no dealer hassle.
              </p>
              <div className="space-y-3 mb-8">
                {[
                  'Seasonal tune-ups and inspections',
                  'Belt, spark plug, and carburetor repairs',
                  'Same-day on-site repair when parts are available',
                  'At-home convenience — no hauling required',
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3 text-sm text-neutral-600">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-arrow-right-circle-fill text-orange-500 text-base" />
                    </div>
                    {item}
                  </div>
                ))}
              </div>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-neutral-900 hover:bg-neutral-800 text-white font-semibold px-7 py-3.5 rounded-lg transition-colors text-sm whitespace-nowrap cursor-pointer"
              >
                Book Your Service
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-20 lg:py-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-10">
            <p className="text-orange-600 text-xs font-bold uppercase tracking-widest mb-3">FAQs</p>
            <h2 className="font-display text-3xl lg:text-4xl font-bold uppercase tracking-tight text-neutral-900">
              Snow Blower Repair FAQs
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-stone-200 rounded-xl overflow-hidden">
                <button
                  className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-stone-50 transition-colors"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span className="font-semibold text-neutral-900 text-sm pr-4">{faq.q}</span>
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className={`${openFaq === i ? 'ri-subtract-line' : 'ri-add-line'} text-orange-600 text-lg`} />
                  </div>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5">
                    <p className="text-neutral-600 text-sm leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24">
        <div className="absolute inset-0">
          <img src={CTA_IMAGE} alt="Snow blower repair" className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-black/75" />
        </div>
        <div className="relative z-10 max-w-2xl mx-auto px-6 text-center">
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-white uppercase tracking-tight mb-5">
            Ready for Hassle-Free Snow Blower Service?
          </h2>
          <p className="text-white/75 text-base leading-relaxed mb-8">
            Road Tech Services brings certified, convenient repair right to your
            driveway. Contact us to schedule.
          </p>
          <Link to="/contact" className="inline-block bg-orange-600 hover:bg-orange-700 text-white font-semibold px-8 py-4 rounded-lg transition-colors whitespace-nowrap cursor-pointer">
            Book Your Service
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
